import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  Flex,
  IconButton,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Select,
  Stack as ChakraStack,
  Tooltip,
} from '@chakra-ui/react';
import Icons from '../../../Icons';
import {
  Row,
  NumberField,
  InfoTip,
  onEnter,
  errorStyle,
  hintStyle,
  rowLabelStyle,
  selectStyle,
} from './SettingControls';
import { useEditor } from 'components/Canvas';
import useTemplateEditorContext from 'scenes/templateEditor/hooks/useTemplateEditorContext';
import {
  stackIdOf,
  nextStackId,
  parseStackSettingsForm,
  describeStackMember,
  memberBoundary,
} from 'helpers/stacks';
import {
  isFixedHeight,
  shrinksToBox,
  shrinkModeOf,
  boxRole,
} from 'helpers/textBox';

const labelStyle = {
  fontSize: '14px',
  paddingBottom: '0.5rem',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

const isText = object => /Text|SuppliedValue/.test(object?.type || '');

// Set when the panel itself changes the selection (clicking a member), so the
// change does not close the popover. Module level rather than a ref because
// the toolbar panel, and this component with it, remounts when the new member
// is a different type of object.
let keepOpenOnNextSelection = false;

// the on-canvas indicator colour, so the button says "this is in a stack"
const MEMBER_COLOR = '#e8590c';

/**
 * The push-down stack control for the selected object(s).
 *
 * Nothing in a stack: make a new stack of the selection, or add a single
 * object to a stack that already exists on its page. In a stack: the members
 * in flow order, the settings (written onto every member), and leaving or
 * dissolving the stack. The stitcher does the layout at render time; the
 * server preview is where the pushed result can be seen.
 */
function Stack() {
  const editor = useEditor();
  const { activeObject, stackMenuOpen, setStackMenuOpen } =
    useTemplateEditorContext();
  // shared with the context menu, which opens this popover too
  const isOpen = stackMenuOpen;
  const onOpen = () => setStackMenuOpen(true);
  const onClose = () => setStackMenuOpen(false);

  useEffect(() => {
    if (keepOpenOnNextSelection) {
      keepOpenOnNextSelection = false;
      return;
    }
    setStackMenuOpen(false);
  }, [activeObject, setStackMenuOpen]);
  // re-read membership after any change to the canvas, including undo
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    if (!editor) return undefined;
    const bump = () => setRevision(r => r + 1);
    editor.on('stacks:changed', bump);
    editor.on('history:changed', bump);
    return () => {
      editor.off('stacks:changed', bump);
      editor.off('history:changed', bump);
    };
  }, [editor]);

  const handler = editor?.handlers?.stacksHandler;
  if (!handler || !activeObject) return null;

  const isSelection = activeObject.type === 'activeSelection';
  const targets = isSelection
    ? (activeObject._objects || []).filter(handler.isEligible)
    : [activeObject];
  if (targets.length === 0) return null;

  const stackId = isSelection ? null : stackIdOf(activeObject);
  const inStack = stackId !== null;

  return (
    <Popover
      placement="bottom-end"
      isLazy
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={onClose}
    >
      <Tooltip
        placement="bottom"
        label={inStack ? `Stack "${stackId}"` : 'Push-down stack'}
      >
        <Box display="inline-block">
          <PopoverTrigger>
            <IconButton
              name="stack"
              color={inStack ? MEMBER_COLOR : '#000000'}
              borderRadius="0"
              variant="ghost"
              size="sm"
              icon={<Icons.Stack size={24} />}
            />
          </PopoverTrigger>
        </Box>
      </Tooltip>

      <Portal>
        <PopoverContent
          borderRadius={0}
          width="340px"
          sx={{ padding: '1rem 1.25rem', maxH: 560, overflowY: 'auto' }}
        >
          {inStack ? (
            <StackSettings
              key={stackId}
              object={activeObject}
              handler={handler}
              closeMenu={onClose}
            />
          ) : (
            <JoinStack
              key={`join:${revision}`}
              targets={targets}
              isSelection={isSelection}
              handler={handler}
              closeMenu={onClose}
            />
          )}
        </PopoverContent>
      </Portal>
    </Popover>
  );
}

/** How a member text shrinks, for the member list. */
const memberTag = member => {
  const tags = [];
  if (isText(member)) {
    if (boxRole(member) === 'reserve') tags.push('reserved height');
    const mode = shrinkModeOf(member);
    if (mode === 'box') tags.push('shrinks to its box');
    if (mode === 'stack') tags.push('shrinks');
  }
  const boundary = memberBoundary(member);
  if (boundary !== null) tags.push(`boundary ${Math.round(boundary)}`);
  return tags.join(' · ') || null;
};

/**
 * The selected member's boundary: a line, in px from the top of the design,
 * its pushed bottom may not pass. Typed values commit when the field is left
 * or on Enter; blank removes it.
 */
function BoundaryRow({ object, handler }) {
  const current = memberBoundary(object);
  const shown = current === null ? '' : String(Math.round(current));
  const [value, setValue] = useState(shown);
  const [error, setError] = useState(null);

  // a change from elsewhere (undo, dragging the line) resets the field
  useEffect(() => setValue(shown), [shown, object]);

  const report = result =>
    setError(
      result.ok ? null : result.error || Object.values(result.errors || {})[0]
    );
  const save = y => report(handler.setBoundary([object], y));

  const commit = () => {
    const text = String(value).trim();
    if (text === shown) return setError(null);
    if (text === '') return save(null);
    const y = Number(text);
    if (!Number.isFinite(y)) return setError('stack boundary must be a number');
    save(y);
  };

  return (
    <>
      <Row
        label="Boundary (px)"
        tip={
          "A line this item can't be pushed below, in px from the top of the card, for example to keep the stack clear of a footer.\n\nIf the text above would push it past the line, that text gets smaller first (when it's set to shrink when the stack is full). If that isn't enough, this item stays at the line and the text runs over it.\n\nItems with the same boundary share one line on the canvas. Leave blank for no boundary."
        }
        error={error}
      >
        <Flex gap="0.35rem" alignItems="center">
          <NumberField
            name="stackBoundary"
            value={value}
            placeholder="none"
            onChange={setValue}
            onCommit={commit}
          />
          {current === null ? (
            <Button
              size="sm"
              borderRadius={0}
              variant="outline"
              name="addBoundary"
              onClick={() => report(handler.addBoundary(object))}
            >
              Add
            </Button>
          ) : (
            <Button
              size="sm"
              borderRadius={0}
              variant="outline"
              name="removeBoundary"
              onClick={() => save(null)}
            >
              Remove
            </Button>
          )}
        </Flex>
      </Row>
      <Box sx={hintStyle}>
        Its designed bottom is at {handler.designedBottom(object)} px; a
        boundary cannot be above that.
      </Box>
    </>
  );
}

/** What the stitcher takes as a text member's designed height. */
function DesignedHeightHint({ object, handler }) {
  const height = handler.designedHeight(object);
  if (shrinksToBox(object)) {
    return (
      <Box sx={hintStyle}>
        Box height {height} px, fixed. Its text shrinks to fit the box, so it
        never pushes the members below it.
      </Box>
    );
  }
  if (isFixedHeight(object)) {
    return (
      <Box sx={hintStyle}>
        Reserved height {height} px. Taller rendered text pushes the members
        below it down.
      </Box>
    );
  }
  return (
    <Box sx={hintStyle}>
      Designed height {height} px, the height of this text on the canvas now.
      Taller rendered text pushes the members below it down. To choose the
      height, turn on Reserve height in the text box settings.
    </Box>
  );
}

function JoinStack({ targets, isSelection, handler, closeMenu }) {
  const pages = new Set(targets.map(target => handler.pageIndexOf(target)));
  const samePage = pages.size === 1;
  const existingIds = samePage ? handler.stackIdsOnPage([...pages][0]) : [];
  const alreadyStacked = targets.filter(o => stackIdOf(o) !== null).length;

  const [name, setName] = useState(nextStackId(existingIds));
  const [joinId, setJoinId] = useState(existingIds[0] || '');
  const [error, setError] = useState(null);

  const report = result => {
    if (result.ok) {
      closeMenu();
      return;
    }
    setError(result.error || Object.values(result.errors || {})[0]);
  };

  return (
    <Box>
      <Box sx={{ fontWeight: 700, fontSize: '14px', paddingBottom: '0.5rem' }}>
        Push-down stack
      </Box>
      <Box sx={{ fontSize: '12px', color: '#666666', paddingBottom: '1rem' }}>
        Lay the design out for its shortest text. When a text in a stack wraps
        to more lines than designed, the members below it are pushed down by the
        extra height. Only stack members move.
      </Box>

      {!samePage && (
        <Box sx={errorStyle}>
          Select objects on the same page. A stack cannot span pages.
        </Box>
      )}

      {samePage && (
        <>
          <Box sx={labelStyle}>
            <Box>
              {isSelection
                ? `New stack of ${targets.length} objects`
                : 'New stack'}
            </Box>
            <InfoTip label="A name for the new stack, shown on the canvas." />
          </Box>
          <Box sx={{ display: 'flex', gap: '0.5rem' }}>
            <Input
              fontSize="14px"
              borderRadius={0}
              size="sm"
              name="stackName"
              value={name}
              onChange={e => setName(e.target.value)}
            />
            <Button
              size="sm"
              borderRadius={0}
              colorScheme="blackAlpha"
              name="createStack"
              onClick={() => report(handler.createStack(targets, name))}
            >
              Create
            </Button>
          </Box>
          {isSelection && targets.length === 1 && (
            <Box sx={hintStyle}>
              Only one of the selected objects can be stacked.
            </Box>
          )}
          {alreadyStacked > 0 && (
            <Box sx={hintStyle}>
              {alreadyStacked === 1
                ? 'One selected object is in a stack already and will leave it.'
                : `${alreadyStacked} selected objects are in stacks already and will leave them.`}
            </Box>
          )}

          {existingIds.length > 0 && (
            <>
              <Divider my="1rem" />
              <Box sx={labelStyle}>
                <Box>Add to an existing stack</Box>
                <InfoTip label="Add the selection to a stack already on this page. It takes on that stack's settings." />
              </Box>
              <Box sx={{ display: 'flex', gap: '0.5rem' }}>
                <Select
                  size="sm"
                  fontSize="14px"
                  borderRadius={0}
                  name="joinStack"
                  sx={selectStyle}
                  value={joinId}
                  onChange={e => setJoinId(e.target.value)}
                >
                  {existingIds.map(id => (
                    <option key={id} value={id}>
                      {id}
                    </option>
                  ))}
                </Select>
                <Button
                  size="sm"
                  borderRadius={0}
                  variant="outline"
                  name="addToStack"
                  onClick={() => report(handler.addToStack(targets, joinId))}
                >
                  Add
                </Button>
              </Box>
            </>
          )}
        </>
      )}

      {error && <Box sx={errorStyle}>{error}</Box>}
    </Box>
  );
}

function StackSettings({ object, handler, closeMenu }) {
  const stackId = stackIdOf(object);
  const members = handler.membersOf(object);
  const settings = handler.settingsOf(object);

  const [name, setName] = useState(stackId);
  const [form, setForm] = useState({
    mode: settings.mode,
    margin: String(settings.margin),
    bottom_margin: String(settings.bottom_margin),
  });
  const [errors, setErrors] = useState({});
  const [error, setError] = useState(null);

  // Every change saves: selects and checkboxes at once, typed fields when
  // left or on Enter. A value the validator rejects stays in the field with
  // its message and is not written.
  const save = nextForm => {
    setError(null);
    const parsed = parseStackSettingsForm(nextForm);
    if (Object.keys(parsed.errors).length) {
      setErrors(parsed.errors);
      return false;
    }
    const updated = handler.updateSettings(object, parsed.settings);
    if (!updated.ok) {
      setErrors(updated.errors || {});
      setError(updated.error || null);
      return false;
    }
    setErrors({});
    return true;
  };

  const setField = (key, value) => setForm(prev => ({ ...prev, [key]: value }));
  const saveForm = () => save(form);
  const setAndSave = (key, value) => {
    const next = { ...form, [key]: value };
    setForm(next);
    save(next);
  };

  const saveName = () => {
    setError(null);
    const renamed = handler.renameStack(object, name);
    if (!renamed.ok) {
      setErrors(prev => ({ ...prev, ...(renamed.errors || {}) }));
      setError(renamed.error || null);
    }
  };

  return (
    <Box>
      <Box sx={{ fontWeight: 700, fontSize: '14px', paddingBottom: '0.75rem' }}>
        Push-down stack
      </Box>

      <Row
        label="Name"
        tip="The stack's name, shown on the canvas. Renaming it renames it for every item in the stack."
        error={errors.id}
      >
        <Input
          fontSize="14px"
          borderRadius={0}
          size="sm"
          width="160px"
          name="stackName"
          value={name}
          onChange={e => setName(e.target.value)}
          onBlur={saveName}
          onKeyDown={onEnter(saveName)}
        />
      </Row>

      <Flex
        alignItems="center"
        justifyContent="space-between"
        paddingTop="0.75rem"
      >
        <Box sx={rowLabelStyle}>
          <Box>Members, top to bottom</Box>
          <InfoTip label="The items in this stack, from the top of the card down. When one grows, everything below it moves down. Items side by side don't push each other. Click one to select it." />
        </Box>
      </Flex>
      <ChakraStack spacing="1px" name="stackMembers" paddingTop="0.25rem">
        {members.map((member, index) => {
          const current = member === object;
          return (
            <Flex key={member.id || index} alignItems="center" gap="0.5rem">
              <Button
                size="xs"
                flex="1"
                variant={current ? 'solid' : 'ghost'}
                colorScheme={current ? 'blackAlpha' : 'gray'}
                borderRadius={0}
                justifyContent="flex-start"
                fontWeight={current ? 600 : 400}
                onClick={() => {
                  if (current) return;
                  keepOpenOnNextSelection = true;
                  handler.select(member);
                }}
              >
                <Box as="span" sx={{ width: '1.5rem', color: '#888888' }}>
                  {index + 1}
                </Box>
                {describeStackMember(member)}
              </Button>
              {memberTag(member) && (
                <Box
                  as="span"
                  fontSize="12px"
                  color="#666666"
                  whiteSpace="nowrap"
                  name="memberShrink"
                >
                  {memberTag(member)}
                </Box>
              )}
            </Flex>
          );
        })}
      </ChakraStack>

      <Box sx={hintStyle}>
        Whether a text shrinks when the stack runs out of room, and how far, is
        set in its text box settings.
      </Box>

      {isText(object) && (
        <DesignedHeightHint object={object} handler={handler} />
      )}

      <BoundaryRow
        key={object.id || stackId}
        object={object}
        handler={handler}
      />

      <Divider my="0.75rem" />

      <Row
        label="Push"
        tip={
          'Choose when a text that gets longer starts pushing the items below it:\n\n• "When text grows": straight away, so the gaps stay as designed.\n\n• "On contact": the text first grows into the empty space below it, and only pushes once it gets within the contact margin of the next item.'
        }
        error={errors.mode}
      >
        <Select
          size="sm"
          fontSize="14px"
          borderRadius={0}
          width="160px"
          name="stackMode"
          sx={selectStyle}
          value={form.mode}
          onChange={e => setAndSave('mode', e.target.value)}
        >
          <option value="preserve_gap">When text grows</option>
          <option value="contact">On contact</option>
        </Select>
      </Row>

      {form.mode === 'contact' && (
        <Row
          label="Contact margin (px)"
          tip="How close a growing text can get to the item below it before it starts pushing that item down."
          error={errors.margin}
        >
          <NumberField
            name="stackMargin"
            value={form.margin}
            onChange={value => setField('margin', value)}
            onCommit={saveForm}
          />
        </Row>
      )}

      <Row
        label="Bottom margin (px)"
        tip="Space to keep clear at the bottom of the card. If the stack would be pushed into it, text set to shrink when the stack is full gets smaller. If it still doesn't fit, the preview shows an error."
        error={errors.bottom_margin}
      >
        <NumberField
          name="stackBottomMargin"
          value={form.bottom_margin}
          onChange={value => setField('bottom_margin', value)}
          onCommit={saveForm}
        />
      </Row>

      {(error || errors.stack) && (
        <Box sx={errorStyle}>{error || errors.stack}</Box>
      )}

      <Divider my="0.75rem" />

      <Box sx={{ display: 'flex', gap: '0.5rem' }}>
        <Button
          name="leaveStack"
          size="sm"
          borderRadius={0}
          variant="outline"
          flex="1"
          onClick={() => {
            handler.removeFromStack([object]);
            closeMenu();
          }}
        >
          Remove from stack
        </Button>
        <Tooltip label="Takes every item out of this stack. Nothing moves on the canvas.">
          <Button
            name="dissolveStack"
            size="sm"
            borderRadius={0}
            variant="outline"
            colorScheme="red"
            flex="1"
            onClick={() => {
              handler.dissolveStack(object);
              closeMenu();
            }}
          >
            Dissolve stack
          </Button>
        </Tooltip>
      </Box>
    </Box>
  );
}

export default Stack;
